import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Target,
  Sprout,
  Headphones,
  MonitorPlay,
  RefreshCw,
  User,
  Users,
  FlaskConical,
  TrendingUp
} from 'lucide-react';

const schema = z.object({
  experience_type: z.enum(['solve_problem', 'improve_state']),
  content_preference: z.enum(['audio', 'video', 'both']),
  work_style: z.enum(['alone', 'with_others']),
  knowledge_preference: z.enum(['learn_why', 'just_experience'])
});

type FormValues = z.infer<typeof schema>;

const STEP_COUNT = 6;

function OptionCard({
  selected,
  onClick,
  children
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex w-full items-start gap-4 rounded-2xl border px-4 py-4 text-left text-sm text-text transition-all duration-200 ease-soft-spring',
        selected
          ? 'border-brandLilac bg-white shadow-soft-elevated ring-2 ring-brandLilac/25'
          : 'border-borderSoft bg-white/90 hover:border-brandSage/40 hover:bg-white'
      ].join(' ')}
    >
      {children}
    </button>
  );
}

export const OnboardingFlowScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const { control, handleSubmit, watch } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      experience_type: 'improve_state',
      content_preference: 'audio',
      work_style: 'alone',
      knowledge_preference: 'just_experience'
    }
  });

  const values = watch();

  const onSubmit = (data: FormValues) => {
    // TODO: send to Supabase onboarding_answers + create profile
    console.log('onboarding complete', data);
    navigate('/app/explore', { replace: true });
  };

  const goNext = () => {
    if (step < STEP_COUNT - 1) {
      setStep((s) => s + 1);
    }
  };

  const goBack = () => {
    if (step === 0) {
      navigate(-1);
    } else {
      setStep((s) => s - 1);
    }
  };

  const stepsArr = Array.from({ length: STEP_COUNT }, (_, i) => i);

  const trackLabel =
    values.experience_type === 'solve_problem'
      ? t('onboarding.trackProblem')
      : t('onboarding.trackGrowth');
  const contentLabel =
    values.content_preference === 'audio'
      ? t('onboarding.contentAudio')
      : values.content_preference === 'video'
        ? t('onboarding.contentVideo')
        : t('onboarding.contentBoth');
  const workLabel =
    values.work_style === 'alone' ? t('onboarding.workSolo') : t('onboarding.workGroup');

  return (
    <div className="flex min-h-svh w-full flex-1 flex-col bg-background px-4 pb-8 pt-8 sm:px-8 sm:pt-10 lg:mx-auto lg:max-w-2xl lg:px-0">
      <header className="mb-6 flex items-center justify-between gap-2 text-xs text-textMuted">
        <button
          type="button"
          onClick={goBack}
          className="shrink-0 font-medium text-brandTeal hover:text-brandDeep"
        >
          {t('common.back')}
        </button>
        <div className="flex min-w-0 flex-1 justify-center gap-1 px-2 sm:px-4">
          {stepsArr.map((s) => (
            <div
              key={s}
              className={`h-1.5 min-w-1.5 flex-1 rounded-full ${s <= step ? 'bg-brandLilac' : 'bg-surfaceMuted'}`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => navigate('/app/explore', { replace: true })}
          className="shrink-0 font-medium text-brandTeal hover:text-brandDeep"
        >
          {t('common.skip')}
        </button>
      </header>

      <form className="flex flex-1 flex-col justify-between" onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          {step === 0 && (
            <section>
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-brandTeal">
                {t('onboarding.step1Title')}
              </p>
              <h2 className="mt-2 text-xl font-semibold text-text sm:text-2xl">
                {t('onboarding.step1Question')}
              </h2>
              <div className="mt-6 space-y-3">
                <Controller
                  control={control}
                  name="experience_type"
                  render={({ field }) => (
                    <>
                      <OptionCard
                        selected={field.value === 'solve_problem'}
                        onClick={() => field.onChange('solve_problem')}
                      >
                        <Target className="mt-0.5 h-8 w-8 shrink-0" strokeWidth={1.75} color="#4A4E69" />
                        <span className="leading-relaxed">{t('onboarding.step1A')}</span>
                      </OptionCard>
                      <OptionCard
                        selected={field.value === 'improve_state'}
                        onClick={() => field.onChange('improve_state')}
                      >
                        <Sprout className="mt-0.5 h-8 w-8 shrink-0" strokeWidth={1.75} color="#84A59D" />
                        <span className="leading-relaxed">{t('onboarding.step1B')}</span>
                      </OptionCard>
                    </>
                  )}
                />
              </div>
            </section>
          )}

          {step === 1 && (
            <section>
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-brandTeal">
                {t('onboarding.step2Title')}
              </p>
              <h2 className="mt-2 text-xl font-semibold text-text sm:text-2xl">
                {t('onboarding.step2Question')}
              </h2>
              <div className="mt-6 space-y-3">
                <Controller
                  control={control}
                  name="content_preference"
                  render={({ field }) => (
                    <>
                      <OptionCard
                        selected={field.value === 'audio'}
                        onClick={() => field.onChange('audio')}
                      >
                        <Headphones className="mt-0.5 h-8 w-8 shrink-0" strokeWidth={1.75} color="#84A59D" />
                        <span className="leading-relaxed">{t('onboarding.step2A')}</span>
                      </OptionCard>
                      <OptionCard
                        selected={field.value === 'video'}
                        onClick={() => field.onChange('video')}
                      >
                        <MonitorPlay
                          className="mt-0.5 h-8 w-8 shrink-0"
                          strokeWidth={1.75}
                          color="#1A535C"
                        />
                        <span className="leading-relaxed">{t('onboarding.step2B')}</span>
                      </OptionCard>
                      <OptionCard
                        selected={field.value === 'both'}
                        onClick={() => field.onChange('both')}
                      >
                        <RefreshCw className="mt-0.5 h-8 w-8 shrink-0" strokeWidth={1.75} color="#EDBB40" />
                        <span className="leading-relaxed">{t('onboarding.step2C')}</span>
                      </OptionCard>
                    </>
                  )}
                />
              </div>
            </section>
          )}

          {step === 2 && (
            <section>
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-brandTeal">
                {t('onboarding.step3Title')}
              </p>
              <h2 className="mt-2 text-xl font-semibold text-text sm:text-2xl">
                {t('onboarding.step3Question')}
              </h2>
              <div className="mt-6 space-y-3">
                <Controller
                  control={control}
                  name="work_style"
                  render={({ field }) => (
                    <>
                      <OptionCard
                        selected={field.value === 'alone'}
                        onClick={() => field.onChange('alone')}
                      >
                        <User className="mt-0.5 h-8 w-8 shrink-0" strokeWidth={1.75} color="#7677E4" />
                        <span className="leading-relaxed">{t('onboarding.step3A')}</span>
                      </OptionCard>
                      <OptionCard
                        selected={field.value === 'with_others'}
                        onClick={() => field.onChange('with_others')}
                      >
                        <Users className="mt-0.5 h-8 w-8 shrink-0" strokeWidth={1.75} color="#3B3385" />
                        <span className="leading-relaxed">{t('onboarding.step3B')}</span>
                      </OptionCard>
                    </>
                  )}
                />
              </div>
            </section>
          )}

          {step === 3 && (
            <section>
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-brandTeal">
                {t('onboarding.step4Title')}
              </p>
              <h2 className="mt-2 text-xl font-semibold text-text sm:text-2xl">
                {t('onboarding.step4Question')}
              </h2>
              <div className="mt-6 space-y-3">
                <Controller
                  control={control}
                  name="knowledge_preference"
                  render={({ field }) => (
                    <>
                      <OptionCard
                        selected={field.value === 'learn_why'}
                        onClick={() => field.onChange('learn_why')}
                      >
                        <FlaskConical
                          className="mt-0.5 h-8 w-8 shrink-0"
                          strokeWidth={1.75}
                          color="#E0642A"
                        />
                        <span className="leading-relaxed">{t('onboarding.step4A')}</span>
                      </OptionCard>
                      <OptionCard
                        selected={field.value === 'just_experience'}
                        onClick={() => field.onChange('just_experience')}
                      >
                        <TrendingUp
                          className="mt-0.5 h-8 w-8 shrink-0"
                          strokeWidth={1.75}
                          color="#1A535C"
                        />
                        <span className="leading-relaxed">{t('onboarding.step4B')}</span>
                      </OptionCard>
                    </>
                  )}
                />
              </div>
            </section>
          )}

          {step === 4 && (
            <section className="space-y-5">
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-brandTeal">
                {t('onboarding.step5Title')}
              </p>
              <h2 className="text-xl font-semibold leading-snug text-text sm:text-2xl">
                {t('onboarding.step5Lead')}
              </h2>
              <p className="text-sm leading-relaxed text-textSecondary sm:text-base">
                {t('onboarding.step5SummaryPrefix')}{' '}
                <span className="font-semibold text-brandDeep">{trackLabel}</span>{' '}
                {t('onboarding.step5SummaryMid')}{' '}
                <span className="font-semibold text-brandDeep">{contentLabel}</span>{' '}
                {t('onboarding.step5SummaryTail')}{' '}
                <span className="font-semibold text-brandDeep">{workLabel}</span>{' '}
                {t('onboarding.step5SummaryEnd')}
              </p>
            </section>
          )}

          {step === 5 && <section className="min-h-[min(30vh,8rem)] flex-1" aria-hidden />}
        </div>

        <div className="mt-8 space-y-3">
          {step === 4 && (
            <p className="text-center text-[11px] text-textMuted">
              You can change these choices any time in your profile.
            </p>
          )}
          {step < STEP_COUNT - 1 && (
            <button
              type="button"
              onClick={goNext}
              className="w-full rounded-full bg-gradient-to-r from-primary to-brandTeal px-4 py-3.5 text-sm font-semibold text-white shadow-soft-elevated transition hover:brightness-[1.03] active:scale-[0.99]"
            >
              {t('common.next')}
            </button>
          )}
          {step === STEP_COUNT - 1 && (
            <button
              type="submit"
              className="w-full rounded-3xl bg-brandLilac px-6 py-4 text-base font-semibold text-white shadow-soft-elevated transition hover:bg-[#6566d4] active:scale-[0.99]"
            >
              {t('onboarding.startTransformation')}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};
